/**
 * TrustGate Policy Diff Modal
 * 
 * Shows governance evolution between policy versions
 * Contract redline aesthetic - not code diff
 * 
 * Demonstrates:
 * - Governance is versioned
 * - Authority was tied to specific rule set
 * - System evolves without rewriting history
 * 
 * "This intent was bound under this policy, but governance has since evolved."
 */

import React from 'react';

interface PolicyChange {
  area: string;
  type: 'added' | 'modified' | 'removed';
  description: string;
}

interface PolicyDiffModalProps {
  bindingVersion: string;
  currentVersion: string;
  onClose: () => void;
}

export const PolicyDiffModal: React.FC<PolicyDiffModalProps> = ({
  bindingVersion,
  currentVersion,
  onClose,
}) => {
  // Mock policy changes - in production, would fetch from governance API
  const getPolicyChanges = (): PolicyChange[] => {
    // Simulated diff between versions
    if (bindingVersion === currentVersion) {
      return [];
    }

    return [
      {
        area: 'Spending Limits',
        type: 'modified',
        description: '↑ Increased daily cap from $10,000 to $25,000',
      },
      {
        area: 'Jurisdiction Rules',
        type: 'added',
        description: 'Added EU sanctions list verification requirement',
      },
      {
        area: 'Approval Threshold',
        type: 'modified',
        description: 'Now requires dual authority for transactions >$50,000',
      },
      {
        area: 'Expiration Defaults',
        type: 'modified',
        description: 'Reduced authorization window from 48h → 24h',
      },
      {
        area: 'Vendor Whitelist',
        type: 'added',
        description: 'Pre-approved vendor list now bypasses manual review',
      },
      {
        area: 'Weekend Processing',
        type: 'removed',
        description: 'Weekend processing restriction lifted',
      },
    ];
  };

  const changes = getPolicyChanges();

  const getChangeIcon = (type: PolicyChange['type']): { icon: string; color: string } => {
    switch (type) {
      case 'added':
        return { icon: '➕', color: 'text-[#10b981]' };
      case 'modified':
        return { icon: '🔄', color: 'text-[#3b82f6]' };
      case 'removed':
        return { icon: '➖', color: 'text-[#ef4444]' };
    }
  };

  const getChangeLabel = (type: PolicyChange['type']): string => {
    switch (type) {
      case 'added':
        return 'New Constraint';
      case 'modified':
        return 'Changed Rule';
      case 'removed':
        return 'Rule Retired';
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-white border-2 border-[#1a1a2e]/20 rounded-lg shadow-2xl w-full max-w-4xl mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header - Contract style */}
        <div className="border-b-2 border-[#1a1a2e]/10 bg-gradient-to-r from-[#f8f9fa] to-[#e9ecef] px-6 py-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-[#1a1a2e] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                📜 TrustGate Policy Evolution
              </h2>
              <div className="flex items-center gap-4 text-sm text-[#64748b]">
                <div>
                  <span className="font-semibold text-[#1a1a2e]">Binding Policy:</span>
                  <span className="ml-2 font-mono">{bindingVersion}</span>
                </div>
                <span>→</span>
                <div>
                  <span className="font-semibold text-[#1a1a2e]">Current Policy:</span>
                  <span className="ml-2 font-mono">{currentVersion}</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-[#64748b] hover:text-[#1a1a2e] transition-colors text-2xl font-light"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content - Contract redline style */}
        <div className="flex-1 overflow-y-auto p-6">
          {changes.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">✓</div>
              <div className="text-lg font-semibold text-[#1a1a2e] mb-2">
                No Policy Changes
              </div>
              <div className="text-sm text-[#64748b]">
                This intent was bound under the current active policy version.
              </div>
            </div>
          ) : (
            <>
              {/* Evolution Message */}
              <div className="bg-[#3b82f6]/10 border border-[#3b82f6]/30 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🔄</span>
                  <div className="flex-1">
                    <div className="font-semibold text-[#1a1a2e] mb-1">
                      Governance Has Evolved Since Binding
                    </div>
                    <div className="text-sm text-[#64748b]">
                      This intent was bound under policy <strong className="text-[#1a1a2e]">{bindingVersion}</strong>, 
                      but governance has since evolved to <strong className="text-[#1a1a2e]">{currentVersion}</strong>.
                      The historical binding remains valid and immutable.
                    </div>
                  </div>
                </div>
              </div>

              {/* Change Legend */}
              <div className="flex items-center gap-6 mb-6 pb-4 border-b border-[#e9ecef]">
                <div className="text-sm font-semibold text-[#64748b]">Change Types:</div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#10b981]">➕</span>
                  <span className="text-[#64748b]">New Constraint</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#3b82f6]">🔄</span>
                  <span className="text-[#64748b]">Changed Rule</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#ef4444]">➖</span>
                  <span className="text-[#64748b]">Rule Retired</span>
                </div>
              </div>

              {/* Sectioned Changes Table */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-[#1a1a2e] mb-4">
                  Policy Changes
                </h3>
                
                <div className="border border-[#e9ecef] rounded-lg overflow-hidden">
                  {/* Table Header */}
                  <div className="bg-[#f8f9fa] border-b border-[#e9ecef] px-4 py-3 flex gap-4">
                    <div className="w-12 text-center text-xs font-semibold text-[#64748b]">
                      Type
                    </div>
                    <div className="w-48 text-xs font-semibold text-[#64748b]">
                      Policy Area
                    </div>
                    <div className="flex-1 text-xs font-semibold text-[#64748b]">
                      Change Description
                    </div>
                  </div>

                  {/* Table Rows */}
                  {changes.map((change, index) => {
                    const { icon, color } = getChangeIcon(change.type);
                    return (
                      <div
                        key={index}
                        className={`px-4 py-4 flex gap-4 border-b border-[#e9ecef] last:border-0 hover:bg-[#f8f9fa]/50 transition-colors ${
                          change.type === 'added' ? 'bg-[#10b981]/5' :
                          change.type === 'removed' ? 'bg-[#ef4444]/5' :
                          'bg-white'
                        }`}
                      >
                        <div className="w-12 flex flex-col items-center justify-start">
                          <span className={`text-2xl ${color}`}>{icon}</span>
                        </div>
                        <div className="w-48">
                          <div className="font-semibold text-[#1a1a2e] text-sm">
                            {change.area}
                          </div>
                          <div className={`text-xs mt-1 ${color}`}>
                            {getChangeLabel(change.type)}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-[#64748b]">
                            {change.description}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Historical Truth Statement */}
              <div className="mt-6 p-4 bg-[#f8f9fa] border border-[#1a1a2e]/10 rounded-lg">
                <div className="flex items-start gap-3">
                  <span className="text-xl">🔒</span>
                  <div className="flex-1 text-sm text-[#64748b]">
                    <strong className="text-[#1a1a2e]">Historical Truth Preserved:</strong>
                    {' '}This intent was evaluated and bound under policy {bindingVersion}. 
                    While governance has evolved, the original binding remains valid and immutable. 
                    This demonstrates that TrustGate governs without rewriting history.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#e9ecef] bg-[#f8f9fa] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-xs text-[#64748b]">
              Policy versioning demonstrates governance maturity
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-[#1a1a2e] hover:bg-[#16213e] text-white rounded font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
